import { collectOffsetPages } from '../../shared/collectOffsetPages';
import { ApiError, type ApiClient } from '../../api/client';
import type {
  RecruitingCollection,
  RecruitingPort,
} from '../../features/recruiting/public';

const pageSize = 100;

type AsyncResult<T> = T extends (...args: infer _Args) => Promise<infer Result> ? Result : never;
type PortResponse<K extends keyof RecruitingPort> = AsyncResult<RecruitingPort[K]>;
type CollectionItem<K extends keyof RecruitingPort> = PortResponse<K> extends RecruitingCollection<infer Item>
  ? Item
  : never;

function jsonMutation(method: string, body: unknown, idempotencyKey: string = crypto.randomUUID()): RequestInit {
  return {
    method,
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(body),
  };
}

async function listAll<T extends { id: string }>(
  client: ApiClient,
  pathForOffset: (offset: number) => string,
): Promise<RecruitingCollection<T>> {
  return collectOffsetPages((offset) => client.requestTenant<RecruitingCollection<T>>(pathForOffset(offset)), pageSize);
}

export function createRecruitingHttpAdapter(client: ApiClient): RecruitingPort {
  return {
    async searchInterviewers(search) {
      const workers = (await client.listAllWorkers(search)).data;
      return workers.map((worker) => ({
        id: worker.id,
        name: worker.descriptor?.trim() || '이름 미등록',
        detail: [worker.primaryJob?.jobProfile?.descriptor || worker.primaryJob?.businessTitle, worker.primaryJob?.supervisoryOrganization?.descriptor].filter(Boolean).join(' · '),
      }));
    },
    getDashboard() {
      return client.requestTenant<PortResponse<'getDashboard'>>('/api/recruiting/v1/{tenant}/dashboard');
    },
    listJobs() {
      return listAll<CollectionItem<'listJobs'>>(
        client,
        (offset) => `/api/recruiting/v1/{tenant}/jobs?limit=${pageSize}&offset=${offset}`,
      );
    },
    listJobStages(jobId) {
      return client.requestTenant<PortResponse<'listJobStages'>>(
        `/api/recruiting/v1/{tenant}/jobs/${encodeURIComponent(jobId)}/stages`,
      );
    },
    createJob(input) {
      return client.requestTenant<PortResponse<'createJob'>>(
        '/api/recruiting/v1/{tenant}/jobs',
        jsonMutation('POST', input),
      );
    },
    updateJobStatus(jobId, status) {
      return client.requestTenant<PortResponse<'updateJobStatus'>>(
        `/api/recruiting/v1/{tenant}/jobs/${encodeURIComponent(jobId)}/status`,
        jsonMutation('POST', { status }),
      );
    },
    listCandidates() {
      return listAll<CollectionItem<'listCandidates'>>(
        client,
        (offset) => `/api/recruiting/v1/{tenant}/candidates?limit=${pageSize}&offset=${offset}`,
      );
    },
    createCandidate(input) {
      return client.requestTenant<PortResponse<'createCandidate'>>(
        '/api/recruiting/v1/{tenant}/candidates',
        jsonMutation('POST', input),
      );
    },
    createApplication(input) {
      return client.requestTenant<PortResponse<'createApplication'>>(
        '/api/recruiting/v1/{tenant}/applications',
        jsonMutation('POST', input),
      );
    },
    listApplications(jobId = '') {
      return listAll<CollectionItem<'listApplications'>>(client, (offset) => {
        const query = new URLSearchParams({ limit: String(pageSize), offset: String(offset) });
        if (jobId) query.set('jobId', jobId);
        return `/api/recruiting/v1/{tenant}/applications?${query}`;
      });
    },
    advanceApplication(applicationId, idempotencyKey = crypto.randomUUID()) {
      return client.requestTenant<PortResponse<'advanceApplication'>>(
        `/api/recruiting/v1/{tenant}/applications/${encodeURIComponent(applicationId)}/advance`,
        jsonMutation('POST', {}, idempotencyKey),
      );
    },
    rejectApplication(applicationId, reason) {
      return client.requestTenant<PortResponse<'rejectApplication'>>(
        `/api/recruiting/v1/{tenant}/applications/${encodeURIComponent(applicationId)}/reject`,
        jsonMutation('POST', { reason }),
      );
    },
    moveApplication(applicationId, input, idempotencyKey = crypto.randomUUID()) {
      return client.requestTenant<PortResponse<'moveApplication'>>(
        `/api/recruiting/v1/{tenant}/applications/${encodeURIComponent(applicationId)}/move`,
        jsonMutation('POST', input, idempotencyKey),
      );
    },
    unrejectApplication(applicationId, idempotencyKey = crypto.randomUUID()) {
      return client.requestTenant<PortResponse<'unrejectApplication'>>(
        `/api/recruiting/v1/{tenant}/applications/${encodeURIComponent(applicationId)}/unreject`,
        jsonMutation('POST', {}, idempotencyKey),
      );
    },
    hireApplication(applicationId, idempotencyKey = crypto.randomUUID()) {
      return client.requestTenant<PortResponse<'hireApplication'>>(
        `/api/recruiting/v1/{tenant}/applications/${encodeURIComponent(applicationId)}/hire`,
        jsonMutation('POST', {}, idempotencyKey),
      );
    },
    listInterviews(limit = pageSize, offset = 0) {
      const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      return client.requestTenant<PortResponse<'listInterviews'>>(
        `/api/recruiting/v1/{tenant}/scheduledInterviews?${query}`,
      );
    },
    listAllInterviews() {
      return listAll<CollectionItem<'listAllInterviews'>>(
        client,
        (offset) => `/api/recruiting/v1/{tenant}/scheduledInterviews?limit=${pageSize}&offset=${offset}`,
      );
    },
    createInterview(input) {
      return client.requestTenant<PortResponse<'createInterview'>>(
        '/api/recruiting/v1/{tenant}/scheduledInterviews',
        jsonMutation('POST', input),
      );
    },
    updateInterviewStatus(interviewId, status, idempotencyKey = crypto.randomUUID()) {
      return client.requestTenant<PortResponse<'updateInterviewStatus'>>(
        `/api/recruiting/v1/{tenant}/scheduledInterviews/${encodeURIComponent(interviewId)}/status`,
        jsonMutation('POST', { status }, idempotencyKey),
      );
    },
    listScorecards() {
      return listAll<CollectionItem<'listScorecards'>>(
        client,
        (offset) => `/api/recruiting/v1/{tenant}/scorecards?limit=${pageSize}&offset=${offset}`,
      );
    },
    submitScorecard(scorecardId, input) {
      return client.requestTenant<PortResponse<'submitScorecard'>>(
        `/api/recruiting/v1/{tenant}/scorecards/${encodeURIComponent(scorecardId)}/submit`,
        jsonMutation('POST', input),
      );
    },
    listOffers() {
      return listAll<CollectionItem<'listOffers'>>(
        client,
        (offset) => `/api/recruiting/v1/{tenant}/offers?limit=${pageSize}&offset=${offset}`,
      );
    },
    createOffer(input) {
      return client.requestTenant<PortResponse<'createOffer'>>(
        '/api/recruiting/v1/{tenant}/offers',
        jsonMutation('POST', input),
      );
    },
    approveOffer(offerId) {
      return client.requestTenant<PortResponse<'approveOffer'>>(
        `/api/recruiting/v1/{tenant}/offers/${encodeURIComponent(offerId)}/approve`,
        jsonMutation('POST', {}),
      );
    },
    resolveOffer(offerId, resolution) {
      return client.requestTenant<PortResponse<'resolveOffer'>>(
        `/api/recruiting/v1/{tenant}/offers/${encodeURIComponent(offerId)}/resolve`,
        jsonMutation('POST', { resolution }),
      );
    },
    isDefinitiveRequestFailure(error) {
      return error instanceof ApiError && error.status >= 400 && error.status < 500;
    },
    isConflict(error) {
      return error instanceof ApiError && error.status === 409;
    },
  };
}
